import React, { useRef, useEffect } from 'react';
import { IoChatbubblesOutline } from "react-icons/io5";
import { MdOutlineArrowCircleUp } from "react-icons/md";
import ReactMarkdown from 'react-markdown';

const ChatInterface = ({ 
    showChat,
    setShowChat,
    chatInput,
    setChatInput,
    chatResponses,
    isChatProcessing,
    handleChatSubmit
}) => {
    const chatContainerRef = useRef(null);

    // Add effect to handle auto-scrolling in chat
    useEffect(() => {
        if (chatContainerRef.current && chatResponses.length > 0) {
            const container = chatContainerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    }, [chatResponses]);

    if (!showChat) return null;

    return (
        <div className="w-[400px] mx-auto flex flex-col items-start justify-start">
            <div className="w-full flex flex-col gap-2 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-rose-100 z-50">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-2">
                    <div className="flex justify-between items-center gap-2 w-full">
                        <h3 className="text-sm font-medium text-gray-700">Chat with TryOn AI</h3>
                        <button
                            onClick={() => setShowChat(false)}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="Close Chat"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                {/* Response Container */}
                <div 
                    ref={chatContainerRef}
                    className="w-full h-[400px] overflow-y-auto mb-2 p-3 bg-gray-50/80 rounded-lg border border-gray-200 scroll-smooth"
                >
                    {chatResponses.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center p-4">
                            <IoChatbubblesOutline className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm text-gray-500">No messages yet</p>
                            <p className="text-xs text-gray-400 mt-1 mb-4">Ask anything about your image</p>
                            <div className="space-y-2 w-full max-w-[280px]">
                                <button
                                    onClick={() => {
                                        setChatInput("Tell me more about this garment.");
                                        document.querySelector('textarea').focus();
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-600 bg-white rounded-lg border border-gray-200 hover:border-rose-200 hover:bg-rose-50/50 transition-colors duration-200"
                                >
                                    Tell me more about this garment
                                </button>
                                <button
                                    onClick={() => {
                                        setChatInput("What is the color of this garment?");
                                        document.querySelector('textarea').focus();
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-600 bg-white rounded-lg border border-gray-200 hover:border-rose-200 hover:bg-rose-50/50 transition-colors duration-200"
                                >
                                    What is the color of this garment?
                                </button>
                                <button
                                    onClick={() => {
                                        setChatInput("What type of fabric is this?");
                                        document.querySelector('textarea').focus();
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-600 bg-white rounded-lg border border-gray-200 hover:border-rose-200 hover:bg-rose-50/50 transition-colors duration-200"
                                >
                                    What type of fabric is this?
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {chatResponses.map((response, index) => (
                                <div key={index} className="animate-fade-in">
                                    <div className="flex items-start gap-2 justify-end">
                                        <div className="flex-1 max-w-[80%]">
                                            <div className="text-sm font-medium text-gray-700 mb-1 text-right">You</div>
                                            <div className="text-sm text-gray-700 bg-rose-50 rounded-lg p-2 border border-rose-100 shadow-sm">
                                                {response.query}
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                                            <IoChatbubblesOutline className="w-5 h-5 text-rose-500" />
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2 mt-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                                            <MdOutlineArrowCircleUp className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1 max-w-[80%]">
                                            <div className="text-sm font-medium text-gray-700 mb-1">TryOn AI</div>
                                            <div className="text-sm text-gray-600 bg-white rounded-lg p-2 border border-gray-200 shadow-sm prose prose-sm max-w-none">
                                                {response.response === 'processing' ? (
                                                    <div className="flex items-center gap-2 text-gray-500">
                                                        <div className="w-4 h-4 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin"></div>
                                                        <span>Processing your request...</span>
                                                    </div>
                                                ) : (
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                                                            ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2" {...props} />,
                                                            ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2" {...props} />,
                                                            li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                                            code: ({ node, inline, ...props }) => 
                                                                inline ? 
                                                                    <code className="bg-gray-100 rounded px-1 py-0.5" {...props} /> :
                                                                    <code className="block bg-gray-100 rounded p-2 my-2 overflow-x-auto" {...props} />,
                                                            pre: ({ node, ...props }) => <pre className="bg-gray-100 rounded p-2 my-2 overflow-x-auto" {...props} />,
                                                            blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-200 pl-4 italic my-2" {...props} />,
                                                            h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-2" {...props} />,
                                                            h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2" {...props} />,
                                                            h3: ({ node, ...props }) => <h3 className="text-base font-bold mb-2" {...props} />,
                                                            a: ({ node, ...props }) => <a className="text-rose-500 hover:text-rose-600 underline" {...props} />,
                                                        }}
                                                    >
                                                        {response.response}
                                                    </ReactMarkdown>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {/* Input Area */}
                <div className="relative flex items-end gap-2">
                    <div className="relative flex-1 justify-center items-center">
                        <textarea
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="What do you want to know about this image? Write your prompt here..."
                            rows={2}
                            disabled={isChatProcessing}
                            className="w-full pl-3 pr-12 py-2 text-sm text-gray-700 bg-white/80 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent resize-none shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !isChatProcessing) {
                                    e.preventDefault();
                                    handleChatSubmit();
                                }
                            }}
                        />
                        <button
                            onClick={handleChatSubmit}
                            disabled={!chatInput.trim() || isChatProcessing}
                            className="absolute top-1/2 -translate-y-1/2 right-2 h-8 w-8 flex items-center justify-center text-white bg-secondary hover:bg-secondary-800 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:hover:bg-secondary disabled:cursor-not-allowed hover:scale-105 active:scale-95 shadow-sm"
                            title="Chat with TryOn AI"
                        >
                            {isChatProcessing ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <MdOutlineArrowCircleUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface; 